import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { crmApi } from "../../lib/crmApi";
import { PageHead } from "./crmUi";

const TABS = [
  ["", "All open"],
  ["today", "Due today"],
  ["overdue", "Overdue"],
  ["upcoming", "Upcoming"],
];

const PRIORITY_COLOR = { high: "#A3141A", medium: "#A6822F", low: "#7A6B5F" };

export default function TasksPage() {
  const [params, setParams] = useSearchParams();
  const due = params.get("due") || "";
  const [tasks, setTasks] = useState(null);
  const [err, setErr] = useState("");

  const reload = useCallback(() => {
    crmApi.tasks(due ? { due } : { status: "open" })
      .then((d) => setTasks(d.tasks))
      .catch((e) => setErr(e.message));
  }, [due]);

  useEffect(() => { reload(); }, [reload]);

  const markDone = async (id) => {
    try { await crmApi.updateTask(id, { status: "done" }); reload(); } catch (e) { setErr(e.message); }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="crm-tasks-page">
      <PageHead eyebrow="Follow-ups" title="Follow-up Tasks" />
      <div className="flex gap-2 mb-4 flex-wrap">
        {TABS.map(([k, l]) => (
          <button
            key={k || "all"}
            type="button"
            data-testid={`crm-tasks-tab-${k || "all"}`}
            onClick={() => {
              const next = new URLSearchParams(params);
              if (k) next.set("due", k); else next.delete("due");
              setParams(next, { replace: true });
            }}
            className={`px-4 py-2 text-[9px] tracking-[0.2em] uppercase border ${
              due === k ? "bg-[#6E1F2C] text-white border-[#6E1F2C]" : "border-[#6E1F2C]/25 text-[#6E1F2C] hover:border-[#6E1F2C]"
            }`}
          >
            {l}
          </button>
        ))}
      </div>
      {err && <p className="text-xs text-[#A3141A]">{err}</p>}
      {!tasks && !err && <p className="text-xs text-[#7A6B5F]">Loading…</p>}
      {tasks && tasks.length === 0 && <p className="text-sm text-[#7A6B5F] italic" data-testid="crm-tasks-empty">Nothing here — all caught up.</p>}
      {tasks && tasks.length > 0 && (
        <ul className="space-y-2">
          {tasks.map((t) => {
            const overdue = t.status === "open" && t.due_date && t.due_date < today;
            return (
              <li
                key={t.id}
                data-testid={`crm-task-row-${t.id}`}
                className={`bg-white border px-4 py-3 flex items-center justify-between gap-3 flex-wrap ${
                  overdue ? "border-[#A3141A]/50" : "border-[#6E1F2C]/10"
                }`}
              >
                <div>
                  <p className="text-[13px] text-[#1C110C]">
                    <strong className="capitalize">{(t.task_type || "task").replace("_", " ")}</strong>
                    {t.lead_id && (
                      <Link to={`/admin/crm/leads/${t.lead_id}`} className="text-[#6E1F2C] hover:underline ml-2">
                        {t.lead_ref} · {t.customer_name || ""}
                      </Link>
                    )}
                  </p>
                  <p className="text-[11px] text-[#7A6B5F] mt-0.5">
                    Due <span className={overdue ? "text-[#A3141A] font-medium" : ""}>{t.due_date}</span>
                    <span className="ml-2 uppercase text-[9px] tracking-[0.14em]" style={{ color: PRIORITY_COLOR[t.priority] }}>{t.priority}</span>
                    {t.assigned_to_name && <span className="ml-2">· {t.assigned_to_name}</span>}
                    {t.notes && <span className="ml-2">— {t.notes}</span>}
                  </p>
                </div>
                {t.status === "open" ? (
                  <button
                    type="button"
                    onClick={() => markDone(t.id)}
                    data-testid={`crm-task-complete-${t.id}`}
                    className="text-[9px] tracking-[0.18em] uppercase text-[#3F6E2C] border border-[#3F6E2C]/40 px-3 py-1.5 hover:bg-[#3F6E2C] hover:text-white"
                  >
                    Mark done
                  </button>
                ) : (
                  <span className="text-[9px] tracking-[0.18em] uppercase text-[#7A6B5F]">{t.status}</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
